import axios from 'axios';
import {
  AppleStateProps,
  AppleTokenProps,
  GoogleStateProps,
  GoogleTokenProps,
  RegistrationProps,
} from '../interfaces/interfaces';
import { jwtDecode } from 'jwt-decode';
import { AppStyle } from './appStyle';
import { appStyleMode, ExternalLoginPrefix } from './clientConfigs';
import { ExternalAuthenticationRequest, ExternalAuthenticationType } from 'orderme-api-integration-client';

const facebookStateKey = 'facebook-oauth-state';
const googleStateKey = 'google-oauth-state';
const googleNonceKey = 'google-oauth-nonce';
const appleStateKey = 'apple-oauth-state';
const appleNonceKey = 'apple-oauth-nonce';

export type FacebookProfileResponse = {
  first_name: string;
  last_name: string;
  email: string;
};

export function randomState(): string {
  const array = new Uint32Array(10);
  window.crypto.getRandomValues(array);
  const state = Array.from(array)
    .map((num) => num.toString(36))
    .join('');
  return addPrefix(state);
}

export function randomNonce(length = 32) {
  let nonce = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const values = window.crypto.getRandomValues(new Uint8Array(length));

  for (let i = 0; i < length; i++) {
    nonce += characters[values[i] % characters.length];
  }
  return addPrefix(nonce);
}

export function saveFacebookState(state: string) {
  if (appStyleMode === AppStyle.Mobile) {
    sessionStorage.setItem(facebookStateKey, state);
  } else {
    localStorage.setItem(facebookStateKey, state);
  }
}

export function getFacebookState() {
  if (appStyleMode === AppStyle.Mobile) {
    return sessionStorage.getItem(facebookStateKey);
  }

  return localStorage.getItem(facebookStateKey);
}

export function removeFacebookState() {
  if (appStyleMode === AppStyle.Mobile) {
    sessionStorage.removeItem(facebookStateKey);
  } else {
    localStorage.removeItem(facebookStateKey);
  }
}

export function saveGoogleState(state: string, nonce: string) {
  if (appStyleMode === AppStyle.Mobile) {
    sessionStorage.setItem(googleStateKey, state);
    sessionStorage.setItem(googleNonceKey, nonce);
  } else {
    localStorage.setItem(googleStateKey, state);
    localStorage.setItem(googleNonceKey, nonce);
  }
}

export function getGoogleState() {
  if (appStyleMode === AppStyle.Mobile) {
    return {
      state: sessionStorage.getItem(googleStateKey),
      nonce: sessionStorage.getItem(googleNonceKey),
    } as GoogleStateProps;
  }

  return {
    state: localStorage.getItem(googleStateKey),
    nonce: localStorage.getItem(googleNonceKey),
  } as GoogleStateProps;
}

export function removeGoogleState() {
  if (appStyleMode === AppStyle.Mobile) {
    sessionStorage.removeItem(googleStateKey);
    sessionStorage.removeItem(googleNonceKey);
  } else {
    localStorage.removeItem(googleStateKey);
    localStorage.removeItem(googleNonceKey);
  }
}

export function saveAppleState(state: string, nonce: string) {
  if (appStyleMode === AppStyle.Mobile) {
    sessionStorage.setItem(appleStateKey, state);
    sessionStorage.setItem(appleNonceKey, nonce);
  } else {
    localStorage.setItem(appleStateKey, state);
    localStorage.setItem(appleNonceKey, nonce);
  }
}

export function getAppleState() {
  if (appStyleMode === AppStyle.Mobile) {
    return {
      state: sessionStorage.getItem(appleStateKey),
      nonce: sessionStorage.getItem(appleNonceKey),
    } as AppleStateProps;
  }
  return {
    state: localStorage.getItem(appleStateKey),
    nonce: localStorage.getItem(appleNonceKey),
  } as AppleStateProps;
}

export function removeAppleState() {
  if (appStyleMode === AppStyle.Mobile) {
    sessionStorage.removeItem(appleStateKey);
    sessionStorage.removeItem(appleNonceKey);
  } else {
    localStorage.removeItem(appleStateKey);
    localStorage.removeItem(appleNonceKey);
  }
}

export function removeAllStates() {
  removeFacebookState();
  removeGoogleState();
}

export async function getFacebookRegistrationProps(accessToken: string, state: string) {
  const { data: res } = await axios.get<FacebookProfileResponse>('https://graph.facebook.com/v17.0/me?', {
    params: {
      access_token: accessToken,
      fields: 'email,first_name,last_name',
      format: 'json',
    },
  });

  return {
    externalLoginType: ExternalAuthenticationType.Facebook,
    externalAuthRequest: { accessToken: accessToken, state: state } as ExternalAuthenticationRequest,
    firstName: res.first_name,
    lastName: res.last_name,
    emailAddress: res.email,
  } as RegistrationProps;
}

export function getGoogleTokenProps(accessToken: string) {
  var tokenProps: GoogleTokenProps = accessToken && jwtDecode(accessToken);
  return tokenProps;
}

export function getGoogleRegistrationProps(accessToken: string, state: string, code: string) {
  var tokenProps = getGoogleTokenProps(accessToken);

  return {
    externalLoginType: ExternalAuthenticationType.Google,
    externalAuthRequest: { accessToken: accessToken, state: state, code: code } as ExternalAuthenticationRequest,
    firstName: tokenProps.given_name,
    lastName: tokenProps.family_name,
    emailAddress: tokenProps.email,
  } as RegistrationProps;
}

export function getAppleRegistrationProps(
  accessToken: string,
  state: string,
  code: string,
  firstName?: string,
  lastName?: string
) {
  var tokenProps = getAppleTokenProps(accessToken);

  return {
    externalLoginType: ExternalAuthenticationType.Apple,
    externalAuthRequest: { accessToken: accessToken, state: state, code: code } as ExternalAuthenticationRequest,
    firstName: firstName,
    lastName: lastName,
    emailAddress: tokenProps.email,
  } as RegistrationProps;
}

export function getAppleTokenProps(accessToken: string) {
  var tokenProps: AppleTokenProps = accessToken && jwtDecode(accessToken);
  return tokenProps;
}

function addPrefix(input: string) {
  return ExternalLoginPrefix ? ExternalLoginPrefix + input : input;
}
